/**
 * **El brazo que va adelante de la ruta, en TODO pedido a `admin-web` que tenga
 * brazo.** `AR5-m` · ⟦el-brazo-entra-por-ruta⟧.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ POR QUÉ EXISTE, Y CUÁL ES EL ESQUEMA BUENO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Palabras del dueño, `[17/9/2026]`, textual:
 *
 *   > *«la idea es que yo elijo un brazo, y de acuerdo al path queda
 *   > seleccionado, ahora tengo dos opciones, hibrido o puro, cualquier otro debe
 *   > dar que no existe»*
 *
 * `admin-web` ya lo hace desde `AR5-m`: toma el PRIMER segmento del camino, lo
 * valida contra los brazos que la instancia declara y **recorta ese segmento
 * sólo si lo acepta** (`apps/admin-web/src/rutas.ts:635`). El que no estaba
 * adaptado era este cliente.
 *
 * ⛔⛔ **HABÍA TRES ESQUEMAS VIVOS Y AHORA HAY UNO.** Medido contra el servidor:
 *
 *   | esquema | quién lo usaba | resultado |
 *   |---|---|---|
 *   | `/padron?…` pelado | casi todos los bordes | `500` |
 *   | `/brazos/hibrido/plantillas` | `borde-plantillas` | `404` |
 *   | **`/hibrido/padron?…`** | nadie | **`200` con datos** ⭐ |
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ POR QUÉ ESTO **NO** CONTRADICE ⟦ni-arm-ni-actor-en-el-cable⟧
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Aquella prohibición existe para que **el cliente no elija un brazo arbitrario**:
 * un `arm` que el navegador manda es un `arm` que el navegador puede cambiar, y
 * ahí el aislamiento del RCT deja de ser una propiedad del servidor.
 *
 * ⭐ Lo que viaja acá **no es una elección libre**: es uno de los brazos que la
 * INSTANCIA declara, y el servidor lo vuelve a validar contra su propia lista
 * (`brazo-compartido.ts`, ⟦las-dos-comprobaciones⟧) antes de tocar un dato. Un
 * segmento inventado no se convierte en un brazo por venir en la URL: da «no
 * existe». Las dos puntas comprueban, y la de este lado **no es la que manda**.
 *
 * ⚠️ Y esta unidad no inventa la lista: sale de `zonas.ts`, que es el conjunto
 * cerrado de la consola. `esBrazo()` distingue mayúsculas a propósito
 * (`R1.1-F1-CA-36`, `R1.1-F2-CA-95`), así que `Hibrido` **no** pasa.
 */
import { SEGMENTOS } from "./rutas.ts"
import { esBrazo, type Brazo } from "./zonas.ts"

/**
 * El brazo que la dirección de la SPA tiene seleccionado, o `null`.
 *
 * ⭐ **La dirección es la fuente, y es la misma que usa la pantalla.** `useBrazo()`
 * lee el mismo segmento por el router; esto lo lee del hash porque los bordes se
 * construyen en la raíz de `App`, **arriba de `<Rutas>`**, donde no hay router del
 * que colgarse. Que las dos lean lo mismo no es una coincidencia que haya que
 * mantener a mano: las dos preguntan por `SEGMENTOS.brazos` + `esBrazo()`.
 *
 * ⛔ `null` es «esta dirección no nombra ningún brazo declarado», y cubre los dos
 * casos que el dueño separó: una zona transversal —que legítimamente no tiene
 * brazo— y un segmento que no es brazo, que tiene que dar «no existe».
 */
export function brazoDeLaDireccion(hash: string = window.location.hash): Brazo | null {
  const partes = hash
    .replace(/^#/, "")
    .split("/")
    .filter((parte) => parte !== "")
  if (partes[0] !== SEGMENTOS.brazos) return null
  const candidato = partes[1]
  return esBrazo(candidato) ? candidato : null
}

/**
 * La raíz de `admin-web` **con el brazo adelante**, para los bordes cuyos datos
 * son de un brazo.
 *
 * ⛔⛔ **LANZA SI NO HAY BRAZO, Y ESO ES LA REPARACIÓN, NO UN EFECTO SECUNDARIO.**
 * Sin brazo el pedido saldría pelado —`/padron`— y eso es exactamente lo que
 * `admin-web` contesta con `500`: un error de servidor por un defecto de cliente,
 * que es la peor forma de fallar porque manda a mirar el lugar equivocado. Dos
 * horas se fueron hoy en discutir qué se había roto, y la respuesta era ésta.
 *
 * ⭐ Los bordes ya envuelven sus `fetch` en `try/catch` y traducen el fallo a un
 * mensaje de pantalla, así que esto llega al operador como «no se pudo», no como
 * una pantalla en blanco.
 *
 * ⚠️ En la práctica no debería lanzar nunca: `App.tsx` no monta ninguna pantalla
 * de brazo si la dirección no nombra uno declarado —`BrazoInexistente` la
 * intercepta antes—, así que un brazo inválido **no llega a pedir datos**. Esto
 * es la segunda comprobación, la que queda escrita para cuando alguien mueva
 * aquélla.
 */
export function raizDelBrazo(adminWebUrl: string, hash?: string): string {
  const brazo = hash === undefined ? brazoDeLaDireccion() : brazoDeLaDireccion(hash)
  if (brazo === null) {
    throw new Error(
      "No se pudo determinar el brazo de la dirección, así que no se pidió nada: " +
        "un pedido sin brazo le pide a `admin-web` datos de ningún brazo.",
    )
  }
  return `${adminWebUrl}/${brazo}`
}
